import { Controller } from "@hotwired/stimulus";

// Contrôleur Stimulus pour gérer les filtres dynamiques des types de congés
export default class extends Controller {
  static targets = ["searchForm", "results"];

  // Délai de debounce en millisecondes
  static values = {
    debounceDelay: { type: Number, default: 100 },
  };

  connect() {
    console.log("Leave types controller connected");
    // Initialisation du timer pour le debounce
    this.debounceTimer = null;

    // Écouter les événements de recherche depuis n'importe où dans le document
    document.addEventListener(
      "leave-types:search",
      this.handleDocumentSearch.bind(this)
    );
    document.addEventListener(
      "leave-types:reset",
      this.handleDocumentReset.bind(this)
    );
  }

  disconnect() {
    // Nettoyer les event listeners
    document.removeEventListener(
      "leave-types:search",
      this.handleDocumentSearch.bind(this)
    );
    document.removeEventListener(
      "leave-types:reset",
      this.handleDocumentReset.bind(this)
    );
  }

  // Gestionnaires d'événements document-wide
  handleDocumentSearch(event) {
    if (event.detail && event.detail.debounce) {
      this.debounceSearch();
    } else {
      this.search(event);
    }
  }

  handleDocumentReset(event) {
    this.resetFilters(event);
  }

  // Méthode pour soumettre le formulaire de recherche
  search(event) {
    console.log("Search event", event);
    // Empêcher la soumission standard du formulaire
    if (event.type === "submit") {
      event.preventDefault();
    }

    // Soumettre le formulaire via Turbo
    this.submitFormWithTurbo();
  }

  // Méthode pour appliquer un délai avant la recherche (debounce)
  debounceSearch() {
    console.log("Debounce search");
    // Annuler tout timer existant
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Créer un nouveau timer
    this.debounceTimer = setTimeout(() => {
      this.submitFormWithTurbo();
    }, this.debounceDelayValue);
  }

  // Méthode pour réinitialiser tous les filtres
  resetFilters(event) {
    console.log("Reset filters");
    event.preventDefault();

    // Réinitialiser tous les champs de recherche (formulaire + modal)
    this.resetAllSearchFields();

    // Soumettre le formulaire pour actualiser les résultats
    this.submitFormWithTurbo();
  }

  // Méthodes utilitaires pour déclencher des événements personnalisés
  triggerSearchEvent(options = {}) {
    const event = new CustomEvent("leave-types:search", {
      detail: options,
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  triggerResetEvent() {
    const event = new CustomEvent("leave-types:reset", {
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  // Méthodes pour être appelées depuis les éléments de la modal
  searchFromModal(event) {
    event.preventDefault();
    this.triggerSearchEvent();
  }

  debounceSearchFromModal(event) {
    this.triggerSearchEvent({ debounce: true });
  }

  resetFiltersFromModal(event) {
    event.preventDefault();
    this.triggerResetEvent();
  }

  // Méthode pour soumettre le formulaire via Turbo
  submitFormWithTurbo() {
    // Créer l'URL avec les paramètres de recherche
    const url = new URL(this.searchFormTarget.action);

    // Collecter tous les champs de recherche (formulaire principal + modal)
    const searchFields = this.getAllSearchFields();

    searchFields.forEach((field) => {
      const value = this.getFieldValue(field);
      if (value) {
        url.searchParams.append(field.name, value);
      }
    });

    // Effectuer la requête avec Turbo
    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        throw new Error("Erreur réseau lors de la recherche");
      })
      .then((html) => {
        Turbo.renderStreamMessage(html);
      })
      .catch((error) => {
        console.error("Erreur lors de la recherche:", error);
      });
  }

  // Méthodes helper pour la gestion des champs
  getAllSearchFields() {
    // Sélectionner tous les champs de recherche dans le contexte du contrôleur
    const selector = 'input[name^="q["], select[name^="q["]';
    return this.element.querySelectorAll(selector);
  }

  getFieldValue(field) {
    if (field.type === "checkbox") {
      return field.checked ? field.value : "";
    } else if (field.type === "radio") {
      return field.checked ? field.value : "";
    } else {
      return field.value.trim();
    }
  }

  resetAllSearchFields() {
    const searchFields = this.getAllSearchFields();

    searchFields.forEach((field) => {
      if (field.type === "checkbox" || field.type === "radio") {
        field.checked = false;
      } else if (field.tagName === "SELECT") {
        // Pour les select, remettre la valeur à vide (option "Tous")
        field.value = "";
        // Déclencher l'événement change pour s'assurer que les listeners sont notifiés
        field.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        field.value = "";
      }
    });
  }
}
